import React from "react";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";

/**
 * Institutional Platform Infrastructure — Cayman platform combining
 * CV5 Capital's CV5 SPC platform and Bell Rock Group's Cayman
 * governance support, plus platform-coordinated counterparties.
 *
 * Layout: TWO featured hero cards (CV5 + Bell Rock) on top, then a
 * 3-column grid of six smaller cards below.
 */

const FEATURED = [
  {
    id: "fund-platform",
    function: "Cayman Fund Platform",
    role: "Preferred Fund Platform Provider",
    name: "CV5 Capital / CV5 SPC",
    location: "Grand Cayman, Cayman Islands",
    description:
      "Institutional Cayman SPC platform for manager-branded hedge fund and private fund strategies, including segregated portfolio structuring, fund documentation, CIMA registration, operational framework and coordinated service-provider infrastructure.",
    status: "Platform engagement in progress",
    tier: "progress",
    href: "https://www.cv5capital.io/en",
    hrefLabel: "cv5capital.io",
    metrics: [
      { label: "Framework", value: "CV5 SPC" },
      { label: "Jurisdiction", value: "Cayman Islands" },
      { label: "Regulator", value: "CIMA" },
    ],
  },
  {
    id: "governance-corporate",
    function: "Cayman Governance · Corporate Services",
    role: "Preferred Governance Provider",
    name: "Bell Rock Group",
    location: "Cayman Islands",
    description:
      "Cayman governance, corporate-services support, registered-office coordination and related fund governance infrastructure supporting the Cayman platform arrangement.",
    status: "Engagement in progress",
    tier: "progress",
    href: "https://www.bellrockgroup.com/",
    hrefLabel: "bellrockgroup.com",
    metrics: [
      { label: "Regulator", value: "CIMA" },
      { label: "Tenure", value: "25+ years" },
      { label: "Track Record", value: "2,000+ entities" },
    ],
  },
];

const RESERVED = [
  {
    id: "governance-board",
    function: "Governance · Compliance · Board Framework",
    name: "CV5 Platform Framework · Bell Rock Support",
    location: "Cayman Islands",
    description:
      "Independent governance framework, board oversight, AML/compliance coordination, valuation policy oversight, regulatory reporting support and service-provider governance.",
    status: "Platform framework in place / engagement in progress",
    tier: "progress",
  },
  {
    id: "onboarding-aml",
    function: "Investor Onboarding · AML/CFT",
    name: "CV5 Capital Platform Process",
    location: "Cayman Islands AML/CFT Standards",
    description:
      "Electronic investor onboarding, subscription processing, KYC/CDD coordination, certification requirements and AML/CFT review under Cayman Islands standards.",
    status: "Platform process in place",
    tier: "active",
  },
  {
    id: "administration",
    function: "Administration · NAV · Investor Reporting",
    name: "Platform-Coordinated Provider",
    location: "Cayman Islands · International",
    description:
      "Independent fund administration, NAV calculation, investor register, transfer agency, subscription/redemption processing and investor reporting coordinated through the CV5 SPC platform.",
    status: "Provided / coordinated through platform",
    tier: "active",
  },
  {
    id: "audit-banking",
    function: "Audit · Banking · Custody · Prime Brokerage",
    name: "Platform-Coordinated Counterparties",
    location: "International",
    description:
      "Audit, banking, custody and prime brokerage relationships coordinated through the platform, subject to the fund strategy, asset class, liquidity profile, AUM level and onboarding requirements.",
    status: "Provided / coordinated through platform",
    tier: "active",
  },
  {
    id: "legal-counsel",
    function: "Legal Counsel",
    name: "External Counsel",
    location: "Cayman Islands · United Kingdom · United States where required",
    description:
      "External legal counsel for manager-level matters, securities-law analysis, offering documentation where required, side letters, investment management agreements and cross-border distribution advice.",
    status: "Selection / engagement discussions in progress",
    tier: "selection",
  },
  {
    id: "cross-border-tax",
    function: "Cross-Border Tax",
    name: "External Tax Adviser",
    location: "International",
    description:
      "Cross-border tax structuring, withholding analysis, FATCA/CRS coordination, investor tax reporting and manager-level tax planning support.",
    status: "Selection in progress",
    tier: "selection",
  },
];

const TIER_STYLE = {
  active: {
    pillClass:
      "border-[var(--kr-gold)] text-[var(--kr-bg)] bg-[var(--kr-gold)]",
    dotClass: "bg-[var(--kr-bg)]",
  },
  progress: {
    pillClass:
      "border-[var(--kr-gold)]/70 text-[var(--kr-gold)] bg-[rgba(201,169,110,0.06)]",
    dotClass: "bg-[var(--kr-gold)]",
  },
  selection: {
    pillClass:
      "border-[var(--kr-line)] text-[var(--kr-text-muted)] bg-transparent",
    dotClass: "bg-[var(--kr-text-muted)]",
  },
};

const StatusPill = ({ tier, status, size = "md" }) => {
  const cfg = TIER_STYLE[tier] || TIER_STYLE.selection;
  const sz =
    size === "sm"
      ? "px-2.5 py-[5px] text-[9px]"
      : "px-3 py-[6px] text-[9.5px]";
  return (
    <span
      className={`inline-flex items-center gap-2 border font-mono-label ${sz} ${cfg.pillClass}`}
    >
      <span
        aria-hidden
        className={`block w-[5px] h-[5px] rounded-full ${cfg.dotClass}`}
      />
      {status}
    </span>
  );
};

/* ------------------------------ FEATURED CARD ------------------------------ */
const FeaturedCard = ({ card }) => (
  <article
    data-testid={`${LANDING.platformInfraCardPrefix}${card.id}`}
    className="relative p-10 lg:p-12 border border-[var(--kr-gold)]/60 overflow-hidden group"
    style={{ background: "rgba(201,169,110,0.04)" }}
  >
    {/* corner brackets */}
    <span className="absolute top-3 left-3 w-5 h-5 border-l border-t border-[var(--kr-gold)]/70" />
    <span className="absolute top-3 right-3 w-5 h-5 border-r border-t border-[var(--kr-gold)]/70" />
    <span className="absolute bottom-3 left-3 w-5 h-5 border-l border-b border-[var(--kr-gold)]/70" />
    <span className="absolute bottom-3 right-3 w-5 h-5 border-r border-b border-[var(--kr-gold)]/70" />

    {/* status row */}
    <div className="flex items-center gap-3 mb-8 flex-wrap">
      <StatusPill tier={card.tier} status={card.status} />
      <span
        aria-hidden
        className="w-8 h-px bg-[var(--kr-gold)]/40 hidden sm:block"
      />
      <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
        Named Counterparty
      </span>
    </div>

    {/* body */}
    <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-4">
      {card.function}
    </div>

    <h3 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.05] mb-4">
      {card.name}
    </h3>

    <div className="font-serif-display italic text-[var(--kr-gold)] text-[1.05rem] mb-6">
      {card.location}
    </div>

    {card.href ? (
      <a
        href={card.href}
        target="_blank"
        rel="noreferrer"
        data-testid={`${LANDING.platformInfraLinkPrefix}${card.id}`}
        className="kr-link font-mono-label text-[11px] mb-8 inline-block"
      >
        {card.hrefLabel} ↗
      </a>
    ) : null}

    <div className="kr-rule mb-6" />

    <p className="text-[var(--kr-text)] text-[15px] leading-[1.8] mb-8 max-w-[58ch]">
      {card.description}
    </p>

    {/* metrics */}
    {card.metrics?.length ? (
      <div className="grid grid-cols-3 gap-6 pt-6 border-t border-[var(--kr-line)]">
        {card.metrics.map((m) => (
          <div key={m.label}>
            <div className="font-mono-label text-[9px] text-[var(--kr-text-muted)] mb-2">
              {m.label}
            </div>
            <div className="font-serif-display text-[var(--kr-text)] text-[1.05rem]">
              {m.value}
            </div>
          </div>
        ))}
      </div>
    ) : null}
  </article>
);

/* ------------------------------ RESERVED CARD ------------------------------ */
const ReservedCard = ({ card }) => (
  <article
    data-testid={`${LANDING.platformInfraCardPrefix}${card.id}`}
    className="relative p-8 lg:p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.025)] transition-colors duration-500 flex flex-col"
  >
    <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-4 leading-[1.4] min-h-[2.4em]">
      {card.function}
    </div>
    <h4 className="font-serif-display text-[var(--kr-text)] text-[1.4rem] leading-[1.15] mb-3">
      {card.name}
    </h4>
    <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[0.95rem] mb-6 min-h-[2.6em]">
      {card.location}
    </div>
    <div className="kr-rule mb-5" />
    <p className="text-[var(--kr-text-muted)] text-[13.5px] leading-[1.7] mb-7 flex-1">
      {card.description}
    </p>
    <div>
      <StatusPill tier={card.tier} status={card.status} size="sm" />
    </div>
  </article>
);

const ServiceProviders = () => {
  return (
    <section
      id="platform-infrastructure"
      data-testid={LANDING.serviceProviders}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
      style={{ background: "var(--kr-bg-2)" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-16 mb-16">
          <div className="lg:col-span-7">
            <SectionHeading
              index="16"
              label="Cayman Platform · Governance · Investor Onboarding"
              title={
                <>
                  Built on{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    institutional
                  </span>{" "}
                  Cayman fund infrastructure.
                </>
              }
            />
          </div>
        </div>

        {/* Intro + Retained-at-the-Firm rail */}
        <div
          data-testid={LANDING.platformInfraIntro}
          className="grid lg:grid-cols-12 gap-12 mb-20"
        >
          <div className="lg:col-span-7 space-y-5 text-[var(--kr-text-muted)] text-[16px] leading-[1.85] max-w-[68ch]">
            <p>
              Kellermann Reid &amp; Co. is progressing a Cayman platform
              arrangement designed to give the firm access to institutional
              fund infrastructure without building every operational layer
              from first principles.
            </p>
            <p>
              The proposed structure combines{" "}
              <a
                href="https://www.cv5capital.io/en"
                target="_blank"
                rel="noreferrer"
                className="kr-link"
              >
                CV5 Capital&rsquo;s CV5 SPC Cayman Islands fund platform
              </a>{" "}
              with{" "}
              <a
                href="https://www.bellrockgroup.com/"
                target="_blank"
                rel="noreferrer"
                className="kr-link"
              >
                Bell Rock Group&rsquo;s
              </a>{" "}
              Cayman governance, corporate-services and fund-platform support,
              subject to final engagement and onboarding, regulatory
              requirements and governance approval.
            </p>
            <p>
              Kellermann Reid &amp; Co. retains responsibility for investment
              strategy, capital formation, portfolio construction, investor
              relationships, brand positioning and manager-level governance.
            </p>
          </div>

          <div className="lg:col-span-5 lg:pl-6">
            <div className="border border-[var(--kr-line)] p-7 lg:p-8 h-full bg-[rgba(10,13,18,0.6)]">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Retained at the Firm
                </span>
                <span className="w-8 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <ul className="space-y-3">
                {[
                  "Investment strategy",
                  "Capital formation",
                  "Portfolio construction",
                  "Investor relationships",
                  "Brand positioning",
                  "Manager-level governance",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[var(--kr-text)] text-[14.5px] leading-[1.55]"
                  >
                    <span
                      aria-hidden
                      className="mt-[8px] block w-[5px] h-[5px] flex-shrink-0"
                      style={{
                        background: "var(--kr-gold)",
                        transform: "rotate(45deg)",
                      }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Two featured cards */}
        <div className="grid lg:grid-cols-2 gap-6 mb-16">
          {FEATURED.map((c) => (
            <FeaturedCard key={c.id} card={c} />
          ))}
        </div>

        {/* Reserved heading */}
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
            Platform-Coordinated · Counterparty Selection Underway
          </span>
          <span className="w-10 h-px bg-[var(--kr-line)]" />
          <span className="font-mono-label text-[10px] text-[var(--kr-text-dim)]">
            Confidential
          </span>
        </div>

        {/* 3-column reserved grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[var(--kr-line)]">
          {RESERVED.map((c) => (
            <ReservedCard key={c.id} card={c} />
          ))}
        </div>

        {/* Disclaimer */}
        <p
          data-testid={LANDING.platformInfraDisclaimer}
          className="text-[var(--kr-text-dim)] text-[12px] leading-[1.75] max-w-[120ch] mt-12"
        >
          Formal appointments, platform access and service-provider
          arrangements remain subject to onboarding, conflict clearance,
          engagement letters, regulatory requirements, strategy approval and
          final governance sign-off. Provider names should not be presented as
          formally appointed unless confirmed in writing.
        </p>
      </div>
    </section>
  );
};

export default ServiceProviders;
